import React, { useEffect, useState, useMemo } from "react";
import { PhotoCamera } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import { fetchBranches, fetchCourses } from "../../../../../utils/Apis";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Avatar,
  Stack,
  FormControl,
  Paper,
} from "@mui/material";

const defaultStudentData = {
  firstName: "",
  lastName: "",
  fatherName: "",
  motherName: "",
  isMobVerified: false,
  isEmailVerified: false,
  dob: "",
  email: "",
  mobileNo: "",
  password: "",
  role: "student",
  branchId: "",
  isFromAdmission: false,
  createdBy: "", // prefill with current admin ID
  profilePic: null,
  studentDetails: {
    batchPeriod: "",
    courses: [],
    feePaid: 0,
    feeTotal: 0,
    feeDue: 0,
    userDiscount: 0,
    status: "active",
  },
  addressDetails: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
  },
};

const AddStudentModal = ({ open, onClose, onSubmit, adminId }) => {
  const dispatch = useDispatch();
  const { branches, courses } = useSelector((state) => state.data);
  const theme = useTheme();

  const [formData, setFormData] = useState({
    ...defaultStudentData,
    createdBy: adminId,
  });

  useEffect(() => {
    if (!branches.length > 0) dispatch(fetchBranches());
    if (!courses.length > 0) dispatch(fetchCourses());
  }, []);

  // Calculate total fee from selected courses
  const calculateTotalFee = useMemo(() => {
    if (!courses?.courses || !formData.studentDetails.courses?.length) return 0;

    return formData.studentDetails.courses.reduce((total, courseId) => {
      const course = courses.courses.find((c) => c._id === courseId);
      const courseFee = course?.feeStructure?.finalFee || 0;
      return total + courseFee;
    }, 0);
  }, [courses, formData.studentDetails.courses]);

  // Update fee calculations when courses or paid amount changes
  useEffect(() => {
    const feeTotal = calculateTotalFee;
    const feePaid = parseFloat(formData.studentDetails.feePaid) || 0;
    const feeDue = feeTotal - feePaid;

    setFormData((prev) => ({
      ...prev,
      studentDetails: {
        ...prev.studentDetails,
        feeTotal,
        feeDue,
      },
    }));
  }, [
    calculateTotalFee,
    formData.studentDetails.feePaid,
    formData.studentDetails.courses,
  ]);

  const handleChange = (e, path = null) => {
    const { name, value, type, checked } = e.target;

    if (path === "studentDetails") {
      setFormData((prev) => ({
        ...prev,
        studentDetails: {
          ...prev.studentDetails,
          [name]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (path === "addressDetails") {
      setFormData((prev) => ({
        ...prev,
        addressDetails: {
          ...prev.addressDetails,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleMultiSelect = (event) => {
    const {
      target: { value },
    } = event;
    setFormData((prev) => ({
      ...prev,
      studentDetails: {
        ...prev.studentDetails,
        courses: typeof value === "string" ? value.split(",") : value,
      },
    }));
  };

  const handleFileChange = (event) => {
    if (event.target.files[0]) {
      // Check file size (Max 50KB)
      if (event.target.files[0].size > 50 * 1024)
        alert("File size must be less than 50KB!");
      else setFormData({ ...formData, profilePic: event.target.files[0] });
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
    setFormData({ ...defaultStudentData, createdBy: adminId });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Add New Student
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ width: "100%" }}>
          {/* Profile Photo Section */}
          <Stack
            direction="column"
            alignItems="center"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Avatar
              alt="Profile Photo"
              src={
                formData.profilePic
                  ? URL.createObjectURL(formData.profilePic)
                  : ""
              }
              sx={{ width: 100, height: 100 }}
            />
            <Button
              variant="contained"
              component="label"
              startIcon={<PhotoCamera />}
            >
              Upload Photo
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                name="profilePic"
                accept=".png, .jpg, .jpeg"
              />
            </Button>
          </Stack>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Personal Information
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>
              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <TextField
                  label="Father Name"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Mother Name"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>
              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <TextField
                  label="DOB"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Contact Information
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Mobile No"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>
            </Grid>

            {/* Branch Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Branch Information
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  name="branchId"
                  value={formData.branchId}
                  onChange={handleChange}
                >
                  {branches?.branches?.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      <div>
                        <p>
                          <strong>{branch.name}</strong>
                        </p>
                        <p>{branch.location.address}</p>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Course Details */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Course & Batch Information
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="Batch Period"
                  name="batchPeriod"
                  value={formData.studentDetails.batchPeriod}
                  onChange={(e) => handleChange(e, "studentDetails")}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Courses</InputLabel>
                  <Select
                    multiple
                    value={formData.studentDetails.courses}
                    onChange={handleMultiSelect}
                    input={<OutlinedInput label="Courses" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const course = courses?.courses?.find(
                            (c) => c._id === id
                          );
                          return <Chip key={id} label={course?.name || id} />;
                        })}
                      </Box>
                    )}
                  >
                    {courses?.courses?.map((course) => (
                      <MenuItem key={course._id} value={course._id}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Fee Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Fee Information
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="Fee Total"
                  name="feeTotal"
                  type="number"
                  value={formData.studentDetails.feeTotal}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated from selected courses"
                  fullWidth
                />
                <TextField
                  label="Fee Paid"
                  name="feePaid"
                  type="number"
                  value={formData.studentDetails.feePaid}
                  onChange={(e) => handleChange(e, "studentDetails")}
                  fullWidth
                />
                <TextField
                  label="Fee Due"
                  name="feeDue"
                  type="number"
                  value={formData.studentDetails.feeDue}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated (Total - Paid)"
                  fullWidth
                />
              </Box>
              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <TextField
                  label="Discount"
                  name="userDiscount"
                  type="number"
                  value={formData.studentDetails.userDiscount}
                  onChange={(e) => handleChange(e, "studentDetails")}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    name="status"
                    value={formData.studentDetails.status}
                    onChange={(e) => handleChange(e, "studentDetails")}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Address Details
              </Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="Street"
                  name="street"
                  value={formData.addressDetails.street}
                  onChange={(e) => handleChange(e, "addressDetails")}
                  fullWidth
                />
                <TextField
                  label="City"
                  name="city"
                  value={formData.addressDetails.city}
                  onChange={(e) => handleChange(e, "addressDetails")}
                  fullWidth
                />
              </Box>
              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <TextField
                  label="State"
                  name="state"
                  value={formData.addressDetails.state}
                  onChange={(e) => handleChange(e, "addressDetails")}
                  fullWidth
                />
                <TextField
                  label="Zip Code"
                  name="zipCode"
                  value={formData.addressDetails.zipCode}
                  onChange={(e) => handleChange(e, "addressDetails")}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            px: 4,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            px: 4,
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentModal;
